import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface RegressionRow {
  readonly regressionSuiteId: string;
  readonly suiteCode: string;
  readonly title: string;
  readonly coveragePct: string | null;
  readonly isImpactBased: boolean;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'ACTIVE', 'ARCHIVED'];

/**
 * M34 — Regression Suite Manager (§31 / §20). Register of regression suites
 * (T458) with coverage percentage, impact-based flag and the
 * draft→active→archived lifecycle. Supports curating a suite and driving it —
 * activate, archive and delete — each wired to the guarded /v1/qms/regression
 * write API. Filterable by status.
 */
export function RegressionSuiteManager(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RegressionRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newImpact, setNewImpact] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: RegressionRow[] }>(`/v1/qms/regression${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m34.regression.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/regression`, {
        method: 'POST',
        body: { title: newTitle.trim(), isImpactBased: newImpact },
        token,
      });
      setNotice(t('m34.regression.created', { title: newTitle.trim() }));
      setNewTitle('');
      setNewImpact(false);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_REGRESSION_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: RegressionRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/regression/${r.regressionSuiteId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m34.regression.notice', { code: r.suiteCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_REGRESSION_MANAGE'));
    }
  };

  const remove = async (r: RegressionRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/regression/${r.regressionSuiteId}`, { method: 'DELETE', token });
      setNotice(t('m34.regression.notice', { code: r.suiteCode, label: t('m34.regression.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_REGRESSION_MANAGE'));
    }
  };

  return (
    <main aria-label="regression-manager">
      <h1>{t('m34.regression.title')}</h1>

      <section aria-label="create-regression">
        <h2>{t('m34.regression.createHeading')}</h2>
        <form aria-label="regression-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.common.title')}<input aria-label="field-suite-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m34.regression.impactBased')}<input type="checkbox" aria-label="field-suite-impact" checked={newImpact} onChange={(e) => setNewImpact(e.target.checked)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-suite" disabled={busy}>{busy ? t('m34.regression.creating') : t('m34.regression.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="regression-action-error">{actionError}</p>}
        {notice && <p aria-label="regression-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="regression-count">{t('m34.regression.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="regression-table"
            rows={rows}
            rowKey={(r) => r.regressionSuiteId}
            columns={[
              { key: 'suiteCode', header: t('m34.common.code'), render: (r) => r.suiteCode },
              { key: 'title', header: t('m34.common.title'), render: (r) => r.title },
              { key: 'coveragePct', header: t('m34.regression.colCoverage'), render: (r) => decimal(r.coveragePct) },
              { key: 'isImpactBased', header: t('m34.regression.colImpact'), render: (r) => r.isImpactBased ? t('m34.common.yes') : t('m34.common.no') },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`activate-${r.suiteCode}`} onClick={() => void act(r, 'activate', t('m34.regression.act.activated'))}>{t('m34.regression.btnActivate')}</Button>
                  )}
                  {r.status === 'ACTIVE' && (
                    <Button variant="danger" type="button" aria-label={`archive-${r.suiteCode}`} onClick={() => void act(r, 'archive', t('m34.regression.act.archived'))}>{t('m34.regression.btnArchive')}</Button>
                  )}
                  {r.status !== 'ARCHIVED' && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.suiteCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
